// Billing API Client for Frontend
// Use Netlify Functions in production, localhost in development
import { getAuthToken } from './auth';
import type {
  CheckoutSession,
  CreatePlanRequest,
  Invoice,
  PhoneSubscriptionSummary,
  PlanStripePriceSyncResult,
  PlanSubscriptionMigrationResult,
  PowerDialerSubscriptionSummary,
  RetryLatestInvoiceResult,
  Subscription,
  SubscriptionPlan,
  UpcomingInvoicePreview,
  UpdatePlanRequest,
} from './billingTypes';

export type {
  CheckoutSession,
  CreatePlanRequest,
  Invoice,
  PhoneSubscriptionSummary,
  PlanStripePriceSyncResult,
  PlanSubscriptionMigrationResult,
  PowerDialerSubscriptionSummary,
  RetryLatestInvoiceResult,
  Subscription,
  SubscriptionPlan,
  UpcomingInvoicePreview,
  UpdatePlanRequest,
} from './billingTypes';

const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3000');
const STRIPE_BILLING_PORTAL_HOST = 'billing.stripe.com';
const BILLING_BACKEND_UNAVAILABLE_MESSAGE =
  'Billing services are temporarily unavailable. Please retry in a few minutes.';
const BILLING_BACKEND_UNAVAILABLE_DEVELOPMENT_HINT =
  ' Start the API backend or set REACT_APP_API_URL.';

function assertStripeBillingPortalUrl(url: string): string {
  const parsed = new URL(url);
  if (parsed.protocol !== 'https:' || parsed.hostname !== STRIPE_BILLING_PORTAL_HOST) {
    throw new Error('Invalid customer portal URL');
  }
  return parsed.toString();
}

function getBillingBackendUnavailableMessage(): string {
  return process.env.NODE_ENV === 'development'
    ? `${BILLING_BACKEND_UNAVAILABLE_MESSAGE}${BILLING_BACKEND_UNAVAILABLE_DEVELOPMENT_HINT}`
    : BILLING_BACKEND_UNAVAILABLE_MESSAGE;
}

function logUnexpectedBillingResponse(response: Response, body: string): void {
  console.error('Billing API returned a non-JSON response:', {
    status: response.status,
    statusText: response.statusText,
    url: response.url,
    body,
  });
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  const trimmed = text.trim();

  if (!trimmed) {
    return {} as T;
  }

  if (trimmed.startsWith('<!doctype') || trimmed.startsWith('<html') || trimmed.startsWith('<')) {
    logUnexpectedBillingResponse(response, text);
    throw new Error(getBillingBackendUnavailableMessage());
  }

  try {
    return JSON.parse(trimmed) as T;
  } catch {
    logUnexpectedBillingResponse(response, text);
    throw new Error(getBillingBackendUnavailableMessage());
  }
}

// Make authenticated API request
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAuthToken();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  }).catch((error) => {
    console.error('Billing API request failed:', error);
    throw new Error(getBillingBackendUnavailableMessage());
  });

  if (!response.ok) {
    let error: { error?: string; message?: string } = {};
    try {
      error = await parseJsonResponse<{ error?: string; message?: string }>(response);
    } catch (parseError) {
      throw parseError;
    }
    throw new Error(error.error || error.message || `HTTP ${response.status}`);
  }

  return parseJsonResponse<T>(response);
}

export const billingApi = {
  // Public: Get active subscription plans (for pricing page) - no auth required
  getPublicPlans: async (): Promise<SubscriptionPlan[]> => {
    // Public endpoint doesn't require auth token
    try {
      const response = await fetch(`${API_BASE_URL}/api/billing/plans`);
      if (!response.ok) {
        let error: { error?: string; message?: string } = {};
        try {
          error = await parseJsonResponse<{ error?: string; message?: string }>(response);
        } catch (parseError) {
          throw parseError;
        }
        throw new Error(error.error || error.message || `HTTP ${response.status}`);
      }
      const data = await parseJsonResponse<any>(response);
      return data.data || data || [];
    } catch (error) {
      console.error('Error fetching plans:', error);
      if (error instanceof Error && error.message === 'Failed to fetch') {
        throw new Error(getBillingBackendUnavailableMessage());
      }
      throw error;
    }
  },

  // Admin: Subscription Plans Management
  getPlans: async (): Promise<SubscriptionPlan[]> => {
    const response = await apiRequest<{ success: boolean; data: SubscriptionPlan[] }>('/api/admin/plans');
    return response.data;
  },

  getPlan: async (id: string): Promise<SubscriptionPlan> => {
    const response = await apiRequest<{ success: boolean; data: SubscriptionPlan }>(`/api/admin/plans/${id}`);
    return response.data;
  },

  createPlan: async (plan: CreatePlanRequest): Promise<SubscriptionPlan> => {
    const response = await apiRequest<{ success: boolean; data: SubscriptionPlan }>('/api/admin/plans', {
      method: 'POST',
      body: JSON.stringify(plan),
    });
    return response.data;
  },

  updatePlan: async (id: string, plan: UpdatePlanRequest): Promise<SubscriptionPlan> => {
    const response = await apiRequest<{ success: boolean; data: SubscriptionPlan }>(`/api/admin/plans/${id}`, {
      method: 'PUT',
      body: JSON.stringify(plan),
    });
    return response.data;
  },

  deletePlan: async (id: string): Promise<SubscriptionPlan> => {
    const response = await apiRequest<{ success: boolean; data: SubscriptionPlan }>(`/api/admin/plans/${id}`, {
      method: 'DELETE',
    });
    return response.data;
  },

  syncPlanStripePrice: async (id: string): Promise<PlanStripePriceSyncResult> => {
    const response = await apiRequest<{ success: boolean; data: PlanStripePriceSyncResult }>(
      `/api/admin/plans/${id}/sync-stripe-price`,
      { method: 'POST' }
    );
    return response.data;
  },

  migratePlanSubscriptions: async (
    id: string,
    options?: {
      apply?: boolean;
      oldPriceId?: string;
      newPriceId?: string;
      prorationBehavior?: 'none' | 'create_prorations' | 'always_invoice';
    }
  ): Promise<PlanSubscriptionMigrationResult> => {
    const response = await apiRequest<{ success: boolean; data: PlanSubscriptionMigrationResult }>(
      `/api/admin/plans/${id}/migrate-subscriptions`,
      {
        method: 'POST',
        body: JSON.stringify({
          apply: options?.apply || false,
          old_price_id: options?.oldPriceId,
          new_price_id: options?.newPriceId,
          proration_behavior: options?.prorationBehavior || 'none',
        }),
      }
    );
    return response.data;
  },

  // Subscription Management
  getSubscription: async (tenantId?: string): Promise<Subscription | null> => {
    try {
      const url = tenantId 
        ? `/api/billing/subscription?tenant_id=${encodeURIComponent(tenantId)}`
        : '/api/billing/subscription';
      const response = await apiRequest<{ success: boolean; data: Subscription }>(url);
      return response.data;
    } catch (error: any) {
      if (error.message.includes('404') || error.message.includes('not found')) {
        return null;
      }
      throw error;
    }
  },

  getPhoneSubscription: async (tenantId?: string): Promise<PhoneSubscriptionSummary | null> => {
    try {
      const url = tenantId
        ? `/api/billing/phone-subscription?tenant_id=${encodeURIComponent(tenantId)}`
        : '/api/billing/phone-subscription';
      const response = await apiRequest<{ success: boolean; data: PhoneSubscriptionSummary | null }>(url);
      return response.data;
    } catch (error: any) {
      if (error.message.includes('404') || error.message.includes('not found')) {
        return null;
      }
      throw error;
    }
  },

  getPowerDialerSubscription: async (tenantId?: string): Promise<PowerDialerSubscriptionSummary | null> => {
    try {
      const url = tenantId
        ? `/api/billing/power-dialer-subscription?tenant_id=${encodeURIComponent(tenantId)}`
        : '/api/billing/power-dialer-subscription';
      const response = await apiRequest<{ success: boolean; data: PowerDialerSubscriptionSummary | null }>(url);
      return response.data;
    } catch (error: any) {
      if (error.message.includes('404') || error.message.includes('not found')) {
        return null;
      }
      throw error;
    }
  },

  adminPhoneSubscriptionBackfill: async (params: {
    clientId?: string;
    clientIds?: string[];
    email?: string;
    runAll?: boolean;
    apply?: boolean;
    limit?: number;
  }): Promise<any> => {
    const payload: Record<string, any> = {};
    if (params.clientId) payload.client_id = params.clientId;
    if (params.clientIds?.length) payload.client_ids = params.clientIds;
    if (params.email) payload.email = params.email;
    if (params.runAll) payload.run_all = true;
    if (params.apply) payload.apply = true;
    if (params.limit != null) payload.limit = params.limit;

    return apiRequest('/api/admin/phone-subscription-backfill', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  adminCreateManualBalanceCharge: async (params: {
    clientId: string;
    amount: number;
    description: string;
    entryType?: 'charge' | 'credit';
    requestId: string;
  }): Promise<{
    client_id: string;
    client_name: string | null;
    client_email: string | null;
    transaction_id: string;
    amount: number;
    description: string;
    entry_type: 'charge' | 'credit';
    previous_balance: number | null;
    new_balance: number | null;
  }> => {
    const response = await apiRequest<{ success: boolean; data: {
      client_id: string;
      client_name: string | null;
      client_email: string | null;
      transaction_id: string;
      amount: number;
      description: string;
      entry_type: 'charge' | 'credit';
      previous_balance: number | null;
      new_balance: number | null;
    } }>('/api/admin/balance-charges', {
      method: 'POST',
      body: JSON.stringify({
        client_id: params.clientId,
        amount: params.amount,
        description: params.description,
        entry_type: params.entryType || 'charge',
        request_id: params.requestId,
      }),
    });
    return response.data;
  },

  adminBillingAudit: async (params?: {
    clientId?: string;
    days?: number | null;
  }): Promise<{
    success: boolean;
    period: { start: string; end: string; days: number | null };
    summary: any;
    accounts: any[];
    drift_messages: any[];
  }> => {
    const query = new URLSearchParams();
    if (params?.clientId) query.set('client_id', params.clientId);
    if (params?.days === null) query.set('days', 'all');
    else if (params?.days != null) query.set('days', String(params.days));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return apiRequest(`/api/admin/billing-audit${suffix}`);
  },

  createCheckoutSession: async (
    priceId: string,
    planId?: string,
    promoCode?: string,
    subscriptionType?: 'platform' | 'power_dialer'
  ): Promise<CheckoutSession> => {
    const response = await apiRequest<{ success: boolean; data: CheckoutSession }>('/api/billing/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify({ priceId, planId, promoCode, subscriptionType }),
    });
    return response.data;
  },

  createSetupIntent: async (
    priceId: string,
    planId?: string,
    subscriptionType?: 'platform' | 'power_dialer'
  ): Promise<{ clientSecret: string; setupIntentId: string }> => {
    const response = await apiRequest<{ success: boolean; data: { clientSecret: string; setupIntentId: string } }>('/api/billing/create-setup-intent', {
      method: 'POST',
      body: JSON.stringify({ priceId, planId, subscriptionType }),
    });
    return response.data;
  },

  createSubscriptionFromSetup: async (
    setupIntentId: string,
    priceId: string,
    planId?: string,
    promoCode?: string,
    subscriptionType?: 'platform' | 'power_dialer'
  ): Promise<{ subscriptionId: string; status: string }> => {
    const response = await apiRequest<{ success: boolean; data: { subscriptionId: string; status: string } }>('/api/billing/create-subscription-from-setup', {
      method: 'POST',
      body: JSON.stringify({ setupIntentId, priceId, planId, promoCode, subscriptionType }),
    });
    return response.data;
  },

  updateSubscription: async (subscriptionId: string, newPriceId: string, tenantId?: string): Promise<Subscription> => {
    const url = tenantId 
      ? `/api/billing/update-subscription?tenant_id=${encodeURIComponent(tenantId)}`
      : '/api/billing/update-subscription';
    const response = await apiRequest<{ success: boolean; data: Subscription }>(url, {
      method: 'POST',
      body: JSON.stringify({ subscription_id: subscriptionId, new_price_id: newPriceId, tenant_id: tenantId }),
    });
    return response.data;
  },

  getInvoices: async (): Promise<Invoice[]> => {
    const response = await apiRequest<{ success: boolean; data: Invoice[] }>('/api/billing/invoices');
    return response.data;
  },

  getUpcomingInvoice: async (tenantId?: string): Promise<UpcomingInvoicePreview | null> => {
    const url = tenantId
      ? `/api/billing/upcoming-invoice?tenant_id=${encodeURIComponent(tenantId)}`
      : '/api/billing/upcoming-invoice';
    const response = await apiRequest<{ success: boolean; data: UpcomingInvoicePreview | null }>(url);
    return response.data;
  },

  retryLatestInvoice: async (tenantId?: string): Promise<RetryLatestInvoiceResult> => {
    const response = await apiRequest<{ success: boolean; data: RetryLatestInvoiceResult }>('/api/billing/retry-latest-invoice', {
      method: 'POST',
      body: JSON.stringify({ tenant_id: tenantId, client_id: tenantId }),
    });
    return response.data;
  },

  cancelSubscription: async (immediately: boolean = false): Promise<Subscription> => {
    const response = await apiRequest<{ success: boolean; data: Subscription }>('/api/billing/cancel-subscription', {
      method: 'POST',
      body: JSON.stringify({ immediately }),
    });
    return response.data;
  },

  // Pause subscription (client-only, cancels at period end)
  pauseSubscription: async (): Promise<{ status: string; cancel_at_period_end: boolean; current_period_end: string | null }> => {
    const response = await apiRequest<{ success: boolean; data: { status: string; cancel_at_period_end: boolean; current_period_end: string | null } }>('/api/billing/pause-subscription', {
      method: 'POST',
    });
    return response.data;
  },

  applyPromoCode: async (code: string, planId?: string, validateOnly?: boolean, subscriptionType?: 'platform' | 'power_dialer'): Promise<{ client_id: string; plan: string } | { code: string; discount_type: string; discount_value: number; discount_amount: number; final_price: number }> => {
    const response = await apiRequest<{ success: boolean; data: any }>(
      '/api/billing/apply-promo',
      {
        method: 'POST',
        body: JSON.stringify({ code, planId, validateOnly, subscriptionType }),
      }
    );
    return response.data;
  },

  // Apply/queue a promo code for an existing subscription (takes effect next invoice; stacks)
  applyPromoToSubscription: async (
    code: string,
    tenantId?: string,
    subscriptionType?: 'platform' | 'power_dialer'
  ): Promise<{ status: 'applied' | 'queued'; code: string; effective_on: string | null }> => {
    const response = await apiRequest<{ success: boolean; data: { status: 'applied' | 'queued'; code: string; effective_on: string | null } }>(
      '/api/billing/apply-promo-to-subscription',
      {
        method: 'POST',
        body: JSON.stringify({ code, tenant_id: tenantId, client_id: tenantId, subscriptionType }),
      }
    );
    return response.data;
  },

  // Balance refill
  createRefillSession: async (amount: number): Promise<CheckoutSession> => {
    const response = await apiRequest<{ success: boolean; data: CheckoutSession }>('/api/billing/create-refill-session', {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
    return response.data;
  },

  // Create refill from Setup Intent (embedded checkout)
  createRefillFromSetup: async (setupIntentId: string, amount: number): Promise<{ success: boolean; data?: { paymentIntentId: string; newBalance: number } }> => {
    const response = await apiRequest<{ success: boolean; data?: { paymentIntentId: string; newBalance: number } }>('/api/billing/create-refill-from-setup', {
      method: 'POST',
      body: JSON.stringify({ setupIntentId, amount }),
    });
    return response;
  },

  // Charge a saved card on file (no card re-entry)
  createRefillWithSavedPaymentMethod: async (
    amount: number,
    paymentMethodId: string,
    tenantId?: string
  ): Promise<{ success: boolean; data?: { paymentIntentId: string; newBalance?: number }; error?: string; code?: string }> => {
    const response = await apiRequest<{ success: boolean; data?: { paymentIntentId: string; newBalance?: number }; error?: string; code?: string }>(
      '/api/billing/create-refill-with-saved-payment-method',
      {
        method: 'POST',
        body: JSON.stringify({
          amount,
          payment_method_id: paymentMethodId,
          tenant_id: tenantId,
          client_id: tenantId, // backward compatibility
        }),
      }
    );
    return response;
  },

  // Open Stripe Customer Portal for managing payment methods (not subscriptions)
  openCustomerPortal: async (tenantId?: string): Promise<void> => {
    try {
      const response = await apiRequest<{ success: boolean; url: string }>('/api/billing/customer-portal', {
        method: 'POST',
        body: JSON.stringify({ tenant_id: tenantId, client_id: tenantId }), // Support both for backward compatibility
      });
      if (response.url) {
        window.location.href = assertStripeBillingPortalUrl(response.url);
      }
    } catch (error) {
      console.error('Failed to open customer portal:', error);
      throw error;
    }
  },

  // Get balance and billing settings
  getBillingSettings: async (): Promise<{
    current_balance: number;
    auto_refill_enabled: boolean;
    auto_refill_threshold: number;
    auto_refill_amount: number;
    has_payment_method: boolean;
  }> => {
    const response = await apiRequest<{ success: boolean; data: any }>('/api/billing/settings');
    return response.data;
  },
};
